import React, { useState, useEffect, useRef } from 'react';
import './NetworkGraph.css';

// A few pastel colors for lines
const PASTEL_COLORS = [
    '#fbb4ae',
    '#b3cde3',
    '#ccebc5',
    '#decbe4',
    '#fed9a6',
    '#ffe9a8',
    '#b9a8d9',
    '#fddaec',
    '#cccccc',
];

const CANVAS_SIZE = 10000;       // the "galaxy" width & height
const CANVAS_CENTER = 5000;     // the middle coordinate of our galaxy

function getRandomFromArray(arr) {
    return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Attempt to place a new node in a random position around (cx, cy)
 * that doesn’t overlap existing nodes by a certain min distance.
 */
function getNonOverlappingPosition(existingNodes, cx, cy, radius) {
    const maxAttempts = 25;
    const minDistance = 120; // minimum distance between node centers
    for (let i = 0; i < maxAttempts; i++) {
        const angle = Math.random() * 2 * Math.PI;
        const r = Math.random() * radius;
        const x = cx + r * Math.cos(angle);
        const y = cy + r * Math.sin(angle);

        // Keep it within the galaxy bounds (0..CANVAS_SIZE)
        if (x < 0 || x > CANVAS_SIZE || y < 0 || y > CANVAS_SIZE) {
            continue; // skip if it falls outside
        }

        // Check distance from existing nodes
        let tooClose = false;
        for (const n of existingNodes) {
            const dx = x - n.x;
            const dy = y - n.y;
            const dist = Math.sqrt(dx * dx + dy * dy);
            if (dist < minDistance) {
                tooClose = true;
                break;
            }
        }
        if (!tooClose) {
            return { x, y };
        }
    }

    // If no suitable position is found, fallback to (cx, cy)
    return { x: cx, y: cy };
}

const NetworkGraph = () => {
    const [nodes, setNodes] = useState([]);
    const [stars, setStars] = useState([]);  // NEW: store generated star elements

    const [hoveredNode, setHoveredNode] = useState(null);
    const [inputConcept, setInputConcept] = useState('');
    const [showInput, setShowInput] = useState(true);
    const [isGenerating, setIsGenerating] = useState(false);

    // For panning the galaxy:
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
    // We offset the large canvas so that (1500, 1500) is near the center of the screen.
    // Adjust these to match your preferred initial viewpoint.
    const [panOffset, setPanOffset] = useState({ x: -4500, y: -4500 });
    const [zoom, setZoom] = useState(1.0); // start at 100%

    const galaxyRef = useRef(null);

    // We start with no default concept.
    // The user will add the first concept via the input box.
    const handleWheel = (e) => {
        // Prevent the page from scrolling
        e.preventDefault();

        // Typically e.deltaY > 0 => user scrolls "down" => we want to zoom out
        // e.deltaY < 0 => user scrolls "up" => we want to zoom in
        // You can tweak the speed factor as you like:
        const zoomSpeed = 0.001;

        let newZoom = zoom - e.deltaY * zoomSpeed;

        // Optionally clamp the zoom factor
        if (newZoom < 0.3) newZoom = 0.3;  // min 30% scale
        if (newZoom > 5.0) newZoom = 5.0;  // max 500% scale

        setZoom(newZoom);
    };

    useEffect(() => {
        // Generate thousands of random stars ONCE when component mounts
        const starElements = [];
        const totalStars = 3000;  // you can go bigger if performance allows
        for (let i = 0; i < totalStars; i++) {
          // random (x, y) within the 10k x 10k
          const x = Math.random() * CANVAS_SIZE;
          const y = Math.random() * CANVAS_SIZE;
          // random star size between 1px and 3px
          const size = 1 + Math.random() * 2;
          // random opacity from 0.3 - 1
          const opacity = 0.3 + Math.random() * 0.7;
    
          starElements.push(
            <div
              key={`star-${i}`}
              className="star"
              style={{
                left: x,
                top: y,
                width: size,
                height: size,
                opacity: opacity,
              }}
            />
          );
        }
        setStars(starElements);
      }, []);

    const handleAddCenterNode = () => {
        if (!inputConcept.trim()) return;

        const newNode = {
            id: Date.now(),
            name: inputConcept.trim(),
            explanation: 'Center concept. Click to expand.',
            x: CANVAS_CENTER,
            y: CANVAS_CENTER,
            parentId: null,
            show: false,
            lineColor: '#ffffff',
        };

        setNodes((prev) => [...prev, newNode]);

        // Fade in after a short delay
        setTimeout(() => {
            setNodes((prev) =>
                prev.map((n) => (n.id === newNode.id ? { ...n, show: true } : n))
            );
        }, 50);

        setShowInput(false);
        setInputConcept('');
    };

    const fetchRelatedConcepts = async (concept) => {
        try {
            const response = await fetch('/api/related-concepts', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ concept }),
            });
            const data = await response.json();
            return data.relatedConcepts || [];
        } catch (err) {
            console.error(err);
            return [];
        }
    };

    const expandConcept = async (nodeId) => {
        const clickedNode = nodes.find((n) => n.id === nodeId);
        if (!clickedNode) return;
      
        setIsGenerating(true);
      
        // 1) Fetch the new related concepts
        const related = await fetchRelatedConcepts(clickedNode.name);
        setIsGenerating(false);
      
        if (!related.length) return;
      
        // 2) Build a set of existing concept names (to handle duplicates)
        const existingNames = new Set(nodes.map((n) => n.name.toLowerCase()));
      
        // 3) Filter out duplicates
        const uniqueRelated = related.filter((item) => {
          return !existingNames.has(item.name.toLowerCase());
        });
      
        // If no unique concepts remain, do nothing
        if (!uniqueRelated.length) return;
      
        // 4) Convert the unique ones into new node objects
        const newNodes = uniqueRelated.map((item) => {
          const pos = getNonOverlappingPosition(
            nodes,
            clickedNode.x,
            clickedNode.y,
            800
          );
          const pastel = getRandomFromArray(PASTEL_COLORS);
      
          return {
            id: Date.now() + Math.random(),
            name: item.name,
            explanation: item.explanation,
            x: pos.x,
            y: pos.y,
            parentId: clickedNode.id,
            show: false,
            lineColor: pastel,
          };
        });
      
        // 3. We'll add them one-by-one, with a small delay between each.
        let i = 0;

        const addNodesSequentially = () => {
            if (i >= newNodes.length) return; // all done

            // (a) Add the next node to state
            const nodeToAdd = newNodes[i];
            setNodes((prev) => [...prev, nodeToAdd]);

            // (b) Short delay to trigger fade-in
            setTimeout(() => {
                // Mark this node's `show = true` to fade in
                setNodes((prev) =>
                    prev.map((n) =>
                        n.id === nodeToAdd.id ? { ...n, show: true } : n
                    )
                );
            }, 100);

            // (c) After a larger delay, move on to the next node
            setTimeout(() => {
                i++;
                addNodesSequentially();
            }, 1000);
        };

        // Start adding them one by one
        addNodesSequentially();
      };
      

    // Draw lines from each node to its parent
    const renderLines = () => {
        return nodes.map((node) => {
            if (node.parentId === null) return null;
            const parentNode = nodes.find((n) => n.id === node.parentId);
            if (!parentNode) return null;

            // Fade line in if the node is "show"
            const lineOpacity = node.show ? 1 : 0;
            return (
                <line
                    key={`line-${node.id}`}
                    x1={parentNode.x}
                    y1={parentNode.y}
                    x2={node.x}
                    y2={node.y}
                    stroke={node.lineColor}
                    strokeWidth="3"
                    style={{
                        transition: 'stroke-opacity 0.7s ease-in-out',
                        strokeOpacity: lineOpacity,
                    }}
                />
            );
        });
    };

    // Mouse handlers for panning the canvas
    const handleMouseDown = (e) => {
        // If we clicked a planet or line, skip panning
        const clickedTag = e.target.tagName.toLowerCase();
        if (
            clickedTag === 'line' ||
            clickedTag === 'circle' ||
            clickedTag === 'rect' ||
            (clickedTag === 'div' && e.target.classList.contains('planet-box'))
        ) {
            return;
        }

        setIsDragging(true);
        setDragStart({ x: e.clientX, y: e.clientY });
    };

    const handleMouseMove = (e) => {
        if (!isDragging) return;
        const dx = e.clientX - dragStart.x;
        const dy = e.clientY - dragStart.y;
        setPanOffset((prev) => ({
            x: prev.x + dx,
            y: prev.y + dy,
        }));
        setDragStart({ x: e.clientX, y: e.clientY });
    };

    const handleMouseUp = () => {
        setIsDragging(false);
    };

    return (
        <div
            className="outer-container w-screen h-screen overflow-hidden bg-black relative"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onWheel={handleWheel}

        >
            {/* "Generating..." indicator */}
            {isGenerating && (
                <div className="absolute top-4 left-4 z-50 text-gray-200 bg-gray-900/90 px-4 py-2 rounded shadow-lg">
                    Generating your planets...
                </div>
            )}

            {/* Input box (hidden once user adds a concept) */}
            {showInput && (
                <div
                    className="
            absolute z-50 top-1/2 left-1/2
            flex flex-row gap-2 items-center
            transform -translate-x-1/2 -translate-y-1/2
          "
                >
                    <input
                        type="text"
                        value={inputConcept}
                        onChange={(e) => setInputConcept(e.target.value)}
                        placeholder="Enter a concept..."
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                                handleAddCenterNode();
                            }
                        }}
                        className="px-3 py-2 rounded border border-gray-300 text-black"
                    />
                    <button
                        onClick={handleAddCenterNode}
                        className="px-4 py-2 bg-white text-black border border-gray-300 rounded hover:bg-gray-100 transition-colors"
                    >
                        Add Concept
                    </button>
                </div>
            )}

            {/* The large "galaxy" canvas, which we pan via transform */}
            <div
                ref={galaxyRef}
                className="galaxy-canvas absolute"
                style={{
                    width: CANVAS_SIZE,
                    height: CANVAS_SIZE,
                    transform: `translate(${panOffset.x}px, ${panOffset.y}px)
          scale(${zoom})`,
                }}
            >
                {stars}

                {/* Lines behind the planets, same size as the canvas */}
                <svg
                    className="absolute top-0 left-0"
                    width={CANVAS_SIZE}
                    height={CANVAS_SIZE}
                >
                    {renderLines()}
                </svg>

                {/* Concept nodes ("planets") anchored at center */}
                {nodes.map((node) => {
                    const showTooltip = hoveredNode === node.id;

                    return (
                        <div
                            key={node.id}
                            className={`
                planet-box
                concept-box
                ${node.show ? 'show' : ''}
                transition-opacity duration-700 ease-in-out
                cursor-pointer
                text-center
                flex items-center justify-center
                text-white
                planet
              `}
                            style={{
                                left: node.x,              // anchor at (x, y)
                                top: node.y,
                                transform: 'translate(-50%, -50%)', // center the element
                            }}
                            onMouseEnter={() => setHoveredNode(node.id)}
                            onMouseLeave={() => setHoveredNode(null)}
                            onClick={(e) => {
                                e.stopPropagation(); // do not start drag
                                expandConcept(node.id);
                            }}
                        >
                            {node.name}
                            {showTooltip && (
                                <div className="tooltip">
                                    {node.explanation}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        </div>
    );
};

export default NetworkGraph;
